import { APIS } from '@constants/apis'
import { useFetch, useStore } from '@core/hooks'
import { isEmpty, omit, get } from 'lodash'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Button, Result } from 'antd'
import { getUser } from '@core/utils/users'
import { ROLE_TYPE } from '@constants/role'
import Loading from '@components/atoms/Loading/Loading'

const withCompanyConfig = (Component) => {
  const EnhancedThemeComponent = (props) => {
    const [config, action] = useStore((state) => state.config)
    const userData = getUser()
    const roleType = get(userData, '_pis.roleType')
    const companyId = get(userData, 'companyId')
    const { companyGroup } = useParams()
    const { t } = useTranslation()
    const contextKey = JSON.stringify([companyGroup, companyId, roleType])
    const { execute: _getConfig } = useFetch(APIS.GENERALS.GET_CONFIG)
    const { execute: _getCompanyGroup } = useFetch(
      APIS.GENERALS.GET_BY_GROUP_CODE
    )
    const { execute: _getAdminConfig } = useFetch(
      APIS.GENERALS.GET_ADMIN_CONFIG
    )
    const requestRef = useRef(null)
    const [loadState, setLoadState] = useState({
      contextKey,
      status: 'idle'
    })

    // Invalidate callbacks before passive effects start work for a new context.
    useLayoutEffect(() => {
      requestRef.current = null
      return () => {
        requestRef.current = null
      }
    }, [contextKey])

    const loadConfig = useCallback(async () => {
      if (requestRef.current?.status === 'loading') return

      const request = { contextKey, status: 'loading' }
      requestRef.current = request
      setLoadState({ contextKey, status: 'loading' })
      const isCurrent = () => requestRef.current === request
      const fail = () => {
        if (!isCurrent()) return
        request.status = 'error'
        setLoadState({ contextKey, status: 'error' })
      }
      const saveConfig = (response) => {
        if (!isCurrent()) return
        const data = response?.data
        if (
          !data ||
          typeof data !== 'object' ||
          Array.isArray(data) ||
          isEmpty(data)
        ) {
          fail()
          return
        }

        // These Redux actions dispatch synchronously; do not yield between them.
        action.setConfig({
          ...data,
          mainSetting: {
            ...omit(data.mainSetting, [
              'mainColorCode',
              'topColorCode',
              'activeColorCode'
            ])
          }
        })
        if (!isCurrent()) return
        action.setTheme({
          mainColorCode: data.mainSetting?.mainColorCode,
          topColorCode: data.mainSetting?.topColorCode,
          activeColorCode: data.mainSetting?.activeColorCode
        })
        if (!isCurrent()) return
        request.status = 'success'
        setLoadState({ contextKey, status: 'success' })
      }

      try {
        if (!companyGroup) {
          fail()
          return
        }
        // Preserve the existing employee/admin company selection rules.
        if (companyId && roleType === ROLE_TYPE.EMPLOYEE) {
          await _getConfig(
            { url: companyId },
            { onSuccess: saveConfig, onFailed: fail }
          )
        } else {
          await _getCompanyGroup(
            { url: companyGroup },
            {
              onSuccess: async (response) => {
                if (!isCurrent()) return
                const adminCompanyId = response?.data?.[0]?.id
                if (!adminCompanyId) {
                  fail()
                  return
                }
                await _getAdminConfig(
                  { url: adminCompanyId },
                  { onSuccess: saveConfig, onFailed: fail }
                )
              },
              onFailed: fail
            }
          )
        }
      } catch {
        fail()
      } finally {
        // useFetch consumes failures, so awaiting it alone is not success.
        if (isCurrent() && request.status === 'loading') fail()
      }
    }, [
      _getAdminConfig,
      _getCompanyGroup,
      _getConfig,
      action,
      companyGroup,
      companyId,
      contextKey,
      roleType
    ])

    const hasConfig = !isEmpty(config)
    useEffect(() => {
      // A failed attempt remains recorded until the user explicitly retries.
      if (!hasConfig && !requestRef.current) loadConfig()
    }, [contextKey, hasConfig, loadConfig])

    const status =
      loadState.contextKey === contextKey ? loadState.status : 'idle'
    if (!hasConfig && status === 'error') {
      return (
        <Result
          status="error"
          title={t('common.companyConfig.failed')}
          extra={
            <Button type="primary" onClick={loadConfig}>
              {t('common.companyConfig.retry')}
            </Button>
          }
        />
      )
    }
    if (!hasConfig || status === 'loading') return <Loading />
    return <Component {...props} />
  }
  return EnhancedThemeComponent
}

export default withCompanyConfig
